import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import {
    getCustomerContentByTag,
    getCustomerContentFiltered,
    getCustomerContentSingle,
    getCustomerContentTag,
    getCustomerContentType,
} from '@/service/PrismicService.js';

import {
    processCategorizePrismicData,
    processPrismicCommonData,
    processPrismicDataForAnamnesis,
    processPrismicDataForBookAppointment,
    processPrismicDataForDashboard,
    processPrismicDataForFindAnAppointment,
    processPrismicDataForFooter,
    processPrismicDataForHeader,
    processPrismicDataForHealthProfile,
    processPrismicFlattenData,
} from '@/utils/processPrismicDataHelpers';

import { useConfigStore, usePersistedPrismicStore, usePrismicStore, useSearchStore } from '@/store.js';

export default function usePrismicContent() {
    const { i18n } = useTranslation();
    const [shouldFetch, setShouldFetch] = useState(false);
    const footerData = usePersistedPrismicStore((state) => state.footerData);
    const headerData = usePersistedPrismicStore((state) => state.headerData);
    const confirmAppointmentData = usePersistedPrismicStore((state) => state.confirmAppointmentData);
    const staticPages = usePersistedPrismicStore((state) => state.staticPages);
    const cookieConsentData = usePersistedPrismicStore((state) => state.cookieConsentData);
    const consentFormsTreatment = usePersistedPrismicStore((state) => state.consentFormsTreatment);
    const heraklesAllForms = usePersistedPrismicStore((state) => state.heraklesAllForms);
    const heraklesCommunication = usePersistedPrismicStore((state) => state.heraklesCommunication);
    const heraklesCourse = usePersistedPrismicStore((state) => state.heraklesCourse);
    const heraklesTherapy = usePersistedPrismicStore((state) => state.heraklesTherapy);
    const heraklesFormsCheckin = usePersistedPrismicStore((state) => state.heraklesFormsCheckin);
    const wielandFormsThankYou = usePersistedPrismicStore((state) => state.wielandFormsThankYou);
    const loginData = usePersistedPrismicStore((state) => state.loginData);
    const registerData = usePersistedPrismicStore((state) => state.registerData);
    const medicalRecordsData = usePersistedPrismicStore((state) => state.medicalRecordsData);
    const healthProfileData = usePersistedPrismicStore((state) => state.healthProfileData);
    const healthData = usePersistedPrismicStore((state) => state.healthData);
    const locationData = usePersistedPrismicStore((state) => state.locationData);
    const accountData = usePersistedPrismicStore((state) => state.accountData);
    const dashboardData = usePersistedPrismicStore((state) => state.dashboardData);
    const bookAppointmentData = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const anamnesisData = usePersistedPrismicStore((state) => state.anamnesisData);
    const treatmentContractWelcomeData = usePersistedPrismicStore((state) => state.treatmentContractWelcomeData);
    const consentData = usePersistedPrismicStore((state) => state.consentData);
    const appointmentData = usePersistedPrismicStore((state) => state.appointmentData);
    const findAppointmentData = usePersistedPrismicStore((state) => state.findAppointmentData);
    const hasHydrated = usePersistedPrismicStore((state) => state._hasHydrated);

    const { data: footerPrismic, ...footerPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !footerData[i18n.language].dataSet)
            ? {
                  key: 'footer-prismic',
                  tag: 'footer_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: headerPrismic, ...headerPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !headerData[i18n.language].dataSet)
            ? {
                  key: 'header-prismic',
                  tag: 'header_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: registerPrismic, ...registerPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !registerData[i18n.language].dataSet)
            ? {
                  key: 'registration-prismic',
                  tag: 'registration_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: loginPrismic, ...loginPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !loginData[i18n.language].dataSet)
            ? {
                  key: 'login-prismic',
                  tag: 'login_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: dashboardPrismic, ...dashboardPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !dashboardData[i18n.language].dataSet)
            ? {
                  key: 'dashboard-prismic',
                  tag: 'dashboard_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: appointmentPrismic, ...appointmentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !appointmentData[i18n.language]?.dataSet)
            ? {
                  key: 'my-appointments-prismic',
                  type: 'appointment_management',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentSingle
    );

    const { data: locationPrismic, ...locationPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !locationData[i18n.language].dataSet)
            ? {
                  key: 'location-prismic',
                  tag: 'loctaion',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: accountPrismic, ...accountPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !accountData[i18n.language].dataSet)
            ? {
                  key: 'account-patients-prismic',
                  tag: 'account_patients',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: confirmAppointmentPrismic, ...confirmAppointmentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !confirmAppointmentData[i18n.language])
            ? {
                  key: 'confirm-appointment-prismic',
                  tag: 'confirm_appointment_patient',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: heraklesTherapyPrismic, ...heraklesTherapyPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !heraklesTherapy[i18n.language])
            ? {
                  key: 'herakles-therapy-prismic',
                  tag: 'herakles_therapy',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: myRecordsPrismic, ...myRecordsPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !medicalRecordsData[i18n.language]?.dataSet)
            ? {
                  key: 'medical-records-prismic',
                  type: 'medical_records',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentSingle
    );
    console.debug('🚀 ~ file: usePrismicContent.js:53 ~ usePrismicContent ~ myRecordsPrismic:', myRecordsPrismic);

    const { data: healthDataPrismic, ...healthDataPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !healthData[i18n.language].dataSet || !anamnesisData[i18n.language].dataSet)
            ? {
                  key: 'health-profile-questions-prismic',
                  tag: 'health_profile_questions',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: healthProfilePrismic, ...healthProfilePrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !healthProfileData[i18n.language]?.dataSet)
            ? {
                  key: 'health-profile-overview-prismic',
                  tag: 'health_profile_overview',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: heraklesCommunicationPrismic, ...heraklesCommunicationPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || heraklesCommunication[i18n.language])
            ? {
                  key: 'herakles-communication-prismic',
                  tag: 'herakles_communication',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const isPrismicConsentDataEmpty = useMemo(
        () => Object.keys(consentData[i18n.language]?.content ?? {}).length === 0,
        [consentData, i18n]
    );

    const { data: consentPrismic, ...consentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !isPrismicConsentDataEmpty)
            ? {
                  key: 'consent-manager-prismic',
                  type: 'consent_manager',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentSingle
    );
    console.debug('🚀 ~ file ~ usePrismicContent ~ anamnesisPrismic:', consentPrismic);

    const { data: consentFormsTreatmentData, ...consentFormsTreatmentState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !consentFormsTreatment[i18n.language])
            ? {
                  key: 'consent-forms-treatment-prismic',
                  tag: 'consent_forms_treatment',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: bookAppointmentPrismic, ...bookAppointmentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !bookAppointmentData[i18n.language].dataSet)
            ? {
                  key: 'book-appointment-prismic',
                  tag: 'book_an_appointment',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: heraklesCoursePrismic, ...heraklesCoursePrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !heraklesCourse[i18n.language])
            ? {
                  key: 'herakles-course-prismic',
                  tag: 'herakles_course',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: anamnesisWelcomeDocument, ...anamnesisWelcomePrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !anamnesisData[i18n.language].dataSet)
            ? {
                  key: 'anamnesis-welcome-prismic',
                  type: 'welcome_page_to_anamnesis_in_hub',
                  params: { lang: i18n.language === 'en' ? 'en-us' : 'de-de' },
              }
            : null,
        getCustomerContentSingle
    );

    const { data: anamnesisThankYouDocument, ...anamnesisThankYouPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !anamnesisData[i18n.language].dataSet)
            ? {
                  key: 'health-profile-thank-you-prismic',
                  type: 'health_profile_thank_you_pages',
                  params: { lang: i18n.language === 'en' ? 'en-us' : 'de-de' },
              }
            : null,
        getCustomerContentSingle
    );

    const { data: heraklesAllFormsPrismic, ...heraklesAllFormsPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !heraklesAllForms[i18n.language])
            ? {
                  key: 'herakles-all-forms-prismic',
                  tag: 'herakles_all_forms',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: heraklesFormsCheckinPrismic, ...heraklesFormsCheckinPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !heraklesFormsCheckin[i18n.language])
            ? {
                  key: 'herakles-forms-checkin-prismic',
                  tag: 'herakles_forms_checkin',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: wielandFormsThankYouPrismic, ...wielandFormsThankYouPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !wielandFormsThankYou[i18n.language])
            ? {
                  key: 'wieland-forms-pages-prismic',
                  tag: 'wieland_forms_welcome_thankyou_pages',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: treatmentContractWelcome, ...treatmentContractWelcomePrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !treatmentContractWelcomeData[i18n.language])
            ? {
                  key: 'treatment-contract-welcome-prismic',
                  tag: 'treatment_contract_welcome_page',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentByTag
    );

    const { data: appConfigPrismic, ...appConfigPrismicState } = useSWRImmutable(
        shouldFetch && hasHydrated
            ? {
                  key: 'patient-app-config-prismic',
                  filters: {
                      'my.config_patient_app.environment': import.meta.env.VITE_ENVIRONMENT,
                  },
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentFiltered
    );

    const { data: searchToolDocument, ...searchToolPrismicState } = useSWRImmutable(
        {
            key: 'search-tool-prismic',
            type: 'search_tool',
            params: {
                lang: i18n.language === 'en' ? 'en-us' : 'de-de',
            },
        },
        getCustomerContentSingle
    );

    const { data: pageNotFoundDocument, ...pageNotFoundPrismicState } = useSWRImmutable(
        {
            key: 'not-found-prismic',
            type: 'not_found_page',
            params: {
                lang: i18n.language === 'en' ? 'en-us' : 'de-de',
            },
        },
        getCustomerContentSingle
    );

    const { data: staticPagesData, ...staticPagesState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !staticPages[i18n.language])
            ? {
                  key: 'static-pages-prismic',
                  filters: { 'my.static_pages.application': 'Patients' },
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                      pageSize: 100,
                  },
              }
            : null,
        getCustomerContentFiltered
    );

    const { data: unsubscribePageDocument, ...unsubscribePagePrismicState } = useSWRImmutable(
        {
            key: 'unsubscribe-prismic',
            type: 'unsubscrib',
            params: {
                lang: i18n.language === 'en' ? 'en-us' : 'de-de',
            },
        },
        getCustomerContentSingle
    );

    const { data: cookieConsentPrismic, ...cookieConsentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !cookieConsentData[i18n.language])
            ? {
                  key: 'cookie-management-prismic',
                  tag: 'cookie_management',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: welcomePageAnamnesisPrismic, ...welcomePageAnamnesisPrismicState } = useSWRImmutable(
        shouldFetch && hasHydrated
            ? {
                  key: 'welcome-page-anamnesis-prismic',
                  tag: 'welcome_page_anamnesis',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentTag
    );

    const { data: findAppointmentPrismic, ...findAppointmentPrismicState } = useSWRImmutable(
        hasHydrated && (shouldFetch || !findAppointmentData[i18n.language].dataSet)
            ? {
                  key: 'find-appointment-prismic-type',
                  type: 'find_an_appointment',
                  params: {
                      lang: i18n.language === 'en' ? 'en-us' : 'de-de',
                  },
              }
            : null,
        getCustomerContentType
    );

    const appVersion = usePersistedPrismicStore((state) => state.appVersion);
    const timestamp = usePersistedPrismicStore((state) => state.timestamp);

    useEffect(() => {
        if (!hasHydrated) return;
        const triggerFetch = async () => {
            const now = Date.now();
            const isVersionMismatch = appVersion !== import.meta.env.VITE_VERSION;
            const timeElapsed = !timestamp || (now - parseInt(timestamp, 10)) / 60000 > 30;

            if (timeElapsed || isVersionMismatch) {
                const newStatePartial = {
                    timestamp: now,
                };

                usePersistedPrismicStore.getState().resetStore();
                newStatePartial.appVersion = import.meta.env.VITE_VERSION;
                setShouldFetch(true);

                usePersistedPrismicStore.setState(newStatePartial);
            }
        };

        triggerFetch();
    }, [hasHydrated]);

    useEffect(() => {
        if (!headerPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.headerData[i18n.language]?.dataSet) return;
        const headerValues = processPrismicDataForHeader(headerPrismic, 'header', 'Patients');
        usePersistedPrismicStore.setState({
            headerData: { ...currentState.headerData, [i18n.language]: headerValues },
        });
    }, [headerPrismic]);

    useEffect(() => {
        if (!footerPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.footerData[i18n.language]?.dataSet) return;
        const footerValues = processPrismicDataForFooter(footerPrismic, 'footer', 'Patients');
        usePersistedPrismicStore.setState({
            footerData: { ...currentState.footerData, [i18n.language]: footerValues },
        });
    }, [footerPrismic]);

    useEffect(() => {
        if (!dashboardPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.dashboardData[i18n.language]?.dataSet) return;
        const dashboardValues = processPrismicDataForDashboard(dashboardPrismic, 'dashboard');
        usePersistedPrismicStore.setState({
            dashboardData: { ...currentState.dashboardData, [i18n.language]: dashboardValues },
        });
    }, [dashboardPrismic]);

    useEffect(() => {
        if (!registerPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.registerData[i18n.language]?.dataSet) return;
        const registerValues = processPrismicFlattenData(registerPrismic, 'registration', 'Patients');
        usePersistedPrismicStore.setState({
            registerData: { ...currentState.registerData, [i18n.language]: registerValues },
        });
    }, [registerPrismic]);

    useEffect(() => {
        if (!loginPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.loginData[i18n.language]?.dataSet) return;
        const loginValues = processPrismicCommonData(loginPrismic, 'login', 'Patients');
        usePersistedPrismicStore.setState({ loginData: { ...currentState.loginData, [i18n.language]: loginValues } });
    }, [loginPrismic]);

    useEffect(() => {
        if (!appointmentPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.appointmentData[i18n.language]?.dataSet) return;
        usePersistedPrismicStore.setState({
            appointmentData: {
                ...currentState.appointmentData,
                [i18n.language]: { content: appointmentPrismic.data, dataSet: true },
            },
        });
    }, [appointmentPrismic]);

    useEffect(() => {
        if (!confirmAppointmentPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.confirmAppointmentData[i18n.language]) return;
        usePersistedPrismicStore.setState({
            confirmAppointmentData: {
                ...currentState.confirmAppointmentData,
                [i18n.language]: confirmAppointmentPrismic,
            },
        });
    }, [confirmAppointmentPrismic]);

    useEffect(() => {
        if (!locationPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.locationData[i18n.language]?.dataSet) return;
        const locationValues = processCategorizePrismicData(locationPrismic);
        console.debug('🚀 ~ file: usePrismicContent.js:52 ~ useEffect ~ locationValues:', locationValues);
        usePersistedPrismicStore.setState({
            locationData: { ...currentState.locationData, [i18n.language]: locationValues },
        });
    }, [locationPrismic]);

    useEffect(() => {
        if (!accountPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.accountData[i18n.language]?.dataSet) return;
        const accountValues = processPrismicCommonData(accountPrismic, 'account_page_patients');
        usePersistedPrismicStore.setState({
            accountData: { ...currentState.accountData, [i18n.language]: accountValues },
        });
    }, [accountPrismic]);

    useEffect(() => {
        if (!healthDataPrismic || !anamnesisWelcomeDocument || !anamnesisThankYouDocument) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.anamnesisData[i18n.language]?.dataSet) return;
        const anamnesisValues = processPrismicDataForAnamnesis(healthDataPrismic);
        usePersistedPrismicStore.setState({
            anamnesisData: {
                ...currentState.anamnesisData,
                [i18n.language]: {
                    ...anamnesisValues,
                    welcomeData: anamnesisWelcomeDocument.data,
                    thankYouData: anamnesisThankYouDocument.data,
                },
            },
        });
    }, [healthDataPrismic, anamnesisWelcomeDocument, anamnesisThankYouDocument]);

    useEffect(() => {
        if (!heraklesTherapyPrismic) return;

        usePersistedPrismicStore.setState({
            heraklesTherapy: { ...heraklesTherapy, [i18n.language]: heraklesTherapyPrismic },
        });
    }, [heraklesTherapyPrismic]);

    useEffect(() => {
        if (!myRecordsPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.medicalRecordsData[i18n.language]?.dataSet) return;
        usePersistedPrismicStore.setState({
            medicalRecordsData: {
                ...currentState.medicalRecordsData,
                [i18n.language]: { content: myRecordsPrismic?.data, dataSet: true },
            },
        });
    }, [myRecordsPrismic]);

    useEffect(() => {
        if (!healthDataPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.healthData[i18n.language]?.dataSet) return;
        const healthDataValues = processCategorizePrismicData(
            healthDataPrismic,
            'health_profile_questionnaire_public_pages',
            'professional_profile'
        );
        console.debug('🚀 ~ file: usePrismicContent.js:111 ~ useEffect ~ healthDataValues:', healthDataValues);
        usePersistedPrismicStore.setState((state) => ({
            healthData: { ...state.healthData, [i18n.language]: healthDataValues },
        }));
    }, [healthDataPrismic]);

    useEffect(() => {
        if (!healthProfilePrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.healthProfileData[i18n.language]?.dataSet) return;
        const healthProfileValues = processPrismicDataForHealthProfile(healthProfilePrismic);
        usePersistedPrismicStore.setState({
            healthProfileData: { ...currentState.healthProfileData, [i18n.language]: healthProfileValues },
        });
    }, [healthProfilePrismic]);

    useEffect(() => {
        if (!heraklesCommunicationPrismic) return;

        usePersistedPrismicStore.setState({
            heraklesCommunication: { ...heraklesCommunication, [i18n.language]: heraklesCommunicationPrismic },
        });
    }, [heraklesCommunicationPrismic]);

    useEffect(() => {
        if (!consentPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (Object.keys(currentState.consentData[i18n.language]?.content ?? {}).length > 0) return;
        usePersistedPrismicStore.setState({
            consentData: {
                ...currentState.consentData,
                [i18n.language]: { loading: !consentPrismic?.data, content: consentPrismic?.data },
            },
        });
    }, [consentPrismic]);

    useEffect(() => {
        if (!findAppointmentPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.findAppointmentData[i18n.language]?.dataSet) return;

        const {
            [i18n.language]: { professionalProfiles, bookingHeading },
        } = currentState.findAppointmentData;

        // Wait for usePrismicCustomerData to populate professionalProfiles before marking dataSet
        if (!professionalProfiles?.length) return;

        const findAppointmentValues = processPrismicDataForFindAnAppointment(
            findAppointmentPrismic,
            professionalProfiles,
            bookingHeading
        );
        usePersistedPrismicStore.setState({
            findAppointmentData: {
                ...currentState.findAppointmentData,
                [i18n.language]: { ...findAppointmentValues, unprocessedData: findAppointmentPrismic },
            },
        });

        console.debug('🚀 ~ file: usePrismicContent.js:64 ~ useEffect ~ findAppointmentData:', findAppointmentValues);
    }, [findAppointmentPrismic]);

    useEffect(() => {
        if (!consentFormsTreatmentData) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.consentFormsTreatment[i18n.language]) return;
        usePersistedPrismicStore.setState({
            consentFormsTreatment: {
                ...currentState.consentFormsTreatment,
                [i18n.language]: consentFormsTreatmentData,
            },
        });
    }, [consentFormsTreatmentData]);

    useEffect(() => {
        if (!bookAppointmentPrismic) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.bookAppointmentData[i18n.language]?.dataSet) return;
        const bookAppointmentValues = processPrismicDataForBookAppointment(bookAppointmentPrismic);
        usePersistedPrismicStore.setState({
            bookAppointmentData: {
                ...currentState.bookAppointmentData,
                [i18n.language]: bookAppointmentValues,
            },
        });
        console.debug('🚀 ~ file: usePrismicContent.js ~ useEffect ~ bookAppointmentValues:', bookAppointmentValues);
    }, [bookAppointmentPrismic]);

    useEffect(() => {
        if (!heraklesCoursePrismic) return;

        usePersistedPrismicStore.setState({
            heraklesCourse: { ...heraklesCourse, [i18n.language]: heraklesCoursePrismic },
        });
    }, [heraklesCoursePrismic]);

    useEffect(() => {
        if (!treatmentContractWelcome) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.treatmentContractWelcomeData[i18n.language]) return;
        usePersistedPrismicStore.setState({
            treatmentContractWelcomeData: {
                ...currentState.treatmentContractWelcomeData,
                [i18n.language]: treatmentContractWelcome?.[0]?.data,
            },
        });
    }, [treatmentContractWelcome]);

    useEffect(() => {
        if (!heraklesAllFormsPrismic) return;

        usePersistedPrismicStore.setState({
            heraklesAllForms: { ...heraklesAllForms, [i18n.language]: heraklesAllFormsPrismic },
        });
    }, [heraklesAllFormsPrismic]);

    useEffect(() => {
        if (!heraklesFormsCheckinPrismic) return;

        usePersistedPrismicStore.setState({
            heraklesFormsCheckin: { ...heraklesFormsCheckin, [i18n.language]: heraklesFormsCheckinPrismic },
        });
    }, [heraklesFormsCheckinPrismic]);

    useEffect(() => {
        if (!wielandFormsThankYouPrismic) return;

        usePersistedPrismicStore.setState({
            wielandFormsThankYou: { ...wielandFormsThankYou, [i18n.language]: wielandFormsThankYouPrismic },
        });
    }, [wielandFormsThankYouPrismic]);

    useEffect(() => {
        if (!appConfigPrismic) return;

        usePersistedPrismicStore.setState({
            featureFlags:
                appConfigPrismic?.[0]?.data.feature_flags?.reduce(
                    (acc, { feature_flag: featureFlag }) => ({
                        ...acc,
                        [featureFlag[0].text]: true,
                    }),
                    {}
                ) ?? {},
        });
    }, [appConfigPrismic]);

    useEffect(() => {
        if (!searchToolDocument) return;

        useSearchStore.setState({ prismicData: searchToolDocument.data });
    }, [searchToolDocument]);

    useEffect(() => {
        if (!pageNotFoundDocument) return;

        usePrismicStore.setState({ pageNotFoundData: pageNotFoundDocument.data });
    }, [pageNotFoundDocument]);

    useEffect(() => {
        if (!staticPagesData) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.staticPages[i18n.language]) return;
        usePersistedPrismicStore.setState({
            staticPages: { ...currentState.staticPages, [i18n.language]: staticPagesData },
        });
    }, [staticPagesData]);

    useEffect(() => {
        if (!unsubscribePageDocument) return;

        usePrismicStore.setState({ unsubscribeData: unsubscribePageDocument.data });
    }, [unsubscribePageDocument]);

    useEffect(() => {
        if (!cookieConsentPrismic || cookieConsentPrismic.length === 0) return;
        const currentState = usePersistedPrismicStore.getState();
        if (currentState.cookieConsentData[i18n.language]) return;

        const formattedData = cookieConsentPrismic.find((entry) => entry.type === 'cookie_management')?.data;
        if (!formattedData) return;

        const privacyId = formattedData.privacy_policy_link?.id;
        const cookieConsentDataWithPrivacyLink = {
            ...formattedData,
            privacy_policy_link_: cookieConsentPrismic.filter((doc) => doc.id === privacyId),
        };

        usePersistedPrismicStore.setState((state) => ({
            cookieConsentData: {
                ...state.cookieConsentData,
                [i18n.language]: cookieConsentDataWithPrivacyLink,
            },
        }));
    }, [cookieConsentPrismic]);

    useEffect(() => {
        if (!welcomePageAnamnesisPrismic) return;

        usePersistedPrismicStore.setState({
            welcomePageAnamnesisData: { data: welcomePageAnamnesisPrismic, dataSet: true },
        });
    }, [welcomePageAnamnesisPrismic]);

    useEffect(() => {
        const allStates = [
            footerPrismicState,
            headerPrismicState,
            registerPrismicState,
            loginPrismicState,
            dashboardPrismicState,
            appointmentPrismicState,
            locationPrismicState,
            accountPrismicState,
            myRecordsPrismicState,
            healthProfilePrismicState,
            healthDataPrismicState,
            consentPrismicState,
            bookAppointmentPrismicState,
            anamnesisWelcomePrismicState,
            anamnesisThankYouPrismicState,
            treatmentContractWelcomePrismicState,
            searchToolPrismicState,
            pageNotFoundPrismicState,
            unsubscribePagePrismicState,
            confirmAppointmentPrismicState,
            staticPagesState,
            consentFormsTreatmentState,
            cookieConsentPrismicState,
            welcomePageAnamnesisPrismicState,
            heraklesAllFormsPrismicState,
            heraklesCommunicationPrismicState,
            heraklesCoursePrismicState,
            heraklesTherapyPrismicState,
            heraklesFormsCheckinPrismicState,
            wielandFormsThankYouPrismicState,
            appConfigPrismicState,
            findAppointmentPrismicState,
        ];

        if (allStates.some(({ error, isLoading }) => !!error && !isLoading)) {
            useConfigStore.setState({ showPrismicFallback: true });
        }
    }, [
        footerPrismicState,
        headerPrismicState,
        registerPrismicState,
        loginPrismicState,
        dashboardPrismicState,
        appointmentPrismicState,
        locationPrismicState,
        accountPrismicState,
        myRecordsPrismicState,
        healthProfilePrismicState,
        healthDataPrismicState,
        consentPrismicState,
        bookAppointmentPrismicState,
        anamnesisWelcomePrismicState,
        anamnesisThankYouPrismicState,
        treatmentContractWelcomePrismicState,
        searchToolPrismicState,
        pageNotFoundPrismicState,
        unsubscribePagePrismicState,
        confirmAppointmentPrismicState,
        staticPagesState,
        consentFormsTreatmentState,
        cookieConsentPrismicState,
        welcomePageAnamnesisPrismicState,
        heraklesAllFormsPrismicState,
        heraklesCommunicationPrismicState,
        heraklesCoursePrismicState,
        heraklesTherapyPrismicState,
        heraklesFormsCheckinPrismicState,
        wielandFormsThankYouPrismicState,
        appConfigPrismicState,
        findAppointmentPrismicState,
    ]);
}
